<script lang="ts" setup>
import { TRACKING_EVENTS } from "@constant"
import { TravauxType } from "@machine/form.machine"
import { TrackingPlugin } from "@plugins/tracking.plugin"
import { useFormStore } from "@stores/form.store"
import { toTypedSchema } from "@vee-validate/yup"
import { useField, useForm } from "vee-validate"
import { computed, inject, onActivated, onDeactivated, ref } from "vue"
import MultiSelect from "primevue/multiselect"
import { object, number, date, string, array, mixed } from "yup"

const { snapshot, send } = useFormStore()

const { $track } = inject<TrackingPlugin>("tracking")!

const travauxRealisesOptions = [
    {
        label: "Oui",
        value: "oui",
    },
    {
        label: "Non",
        value: "non",
    },
]

const travauxOptionsByCategory = [
    {
        label: "Isolation",
        items: [
            {
                label: "Isolation combles / toiture",
                value: "isolation_combles_toiture",
            },
            { label: "Isolation murs", value: "isolation_murs" },
            { label: "Isolation plancher", value: "isolation_plancher" },
        ] as { label: string; value: TravauxType }[],
    },
    {
        label: "Menuiserie",
        items: [
            {
                label: "Fenêtres / portes-fenêtres",
                value: "fenetres_portes_fenetres",
            },
        ] as { label: string; value: TravauxType }[],
    },
    {
        label: "Chauffage et régulation",
        items: [
            { label: "PAC air/eau", value: "pac_air_eau" },
            { label: "PAC eau/eau ou sol/eau", value: "pac_eau_eau_sol_eau" },
            { label: "PAC hybride", value: "pac_hybride" },
            { label: "PAC air/air", value: "pac_air_air" },
            { label: "Chauffage bois", value: "chauffage_bois" },
            { label: "Chaudière biomasse", value: "chaudiere_biomasse" },
            {
                label: "Système solaire combiné",
                value: "systeme_solaire_combine",
            },
            {
                label: "Chauffe-eau thermodynamique",
                value: "chauffe_eau_thermodynamique",
            },
        ] as { label: string; value: TravauxType }[],
    },
]

const travauxTypeValues: TravauxType[] = travauxOptionsByCategory.flatMap(
    (category) => category.items.map((item) => item.value)
)

// 🧾 Schéma de validation
const { errors, handleSubmit } = useForm({
    validationSchema: toTypedSchema(
        object({
            travaux_realises: string().default("oui"),
            start_date: date()
                .required("Sélectionnez une date de début")
                .test(
                    "is-before",
                    "La date de début des travaux doit être antérieure à la date de fin des travaux",
                    function (value) {
                        if (!value) return true
                        const { end_date } = this.parent
                        return value < end_date
                    }
                )
                .default(
                    snapshot.value.context.periode_travaux?.length
                        ? snapshot.value.context.periode_travaux[0]
                        : null
                ),
            end_date: date()
                .test(
                    "is-greater",
                    "La date de fin des travaux doit être ultérieure à la date de début des travaux",
                    function (value) {
                        if (!value) return true
                        const { start_date } = this.parent
                        return value > start_date
                    }
                )
                .required("Sélectionnez une date de fin")
                .default(
                    snapshot.value.context.periode_travaux?.length
                        ? snapshot.value.context.periode_travaux?.[1]
                        : null
                ),
            nombre_occupant: number()
                .required("Indiquez le nombre d'occupants")
                .default(snapshot.value.context.nombre_occupant),
            nombre_occupant_0_18_ans: number()
                .default(snapshot.value.context.nombre_occupant_0_18_ans)
                .nullable(),
            nombre_occupant_18_60_ans: number()
                .default(snapshot.value.context.nombre_occupant_18_60_ans)
                .nullable(),
            nombre_occupant_60_ans: number()
                .default(snapshot.value.context.nombre_occupant_60_ans)
                .nullable(),
            surface_logement: number()
                .default(snapshot.value.context.surface_logement)
                .nullable(),
            travaux_types: array(
                mixed<TravauxType>().oneOf([...travauxTypeValues])
            )
                .min(1, "Sélectionnez au moins un type de travaux")
                .required("Sélectionnez au moins un type de travaux")
                .default(snapshot.value.context.travaux_types || []),
        })
    ),
})

function onError() {
    $track(TRACKING_EVENTS.FORM_INAVLID, {
        formId: "FormInfoLogementGeneral",
        step: snapshot.value.context.step + 1,
    })
}

function onSuccess(values: any) {
    const selectedTravaux = (values.travaux_types || []) as TravauxType[]

    // Mise à jour du context
    send({
        type: "UPDATE_FIELD",
        field: "nombre_occupant",
        value: values.nombre_occupant,
    })
    send({
        type: "UPDATE_FIELD",
        field: "nombre_occupant_0_18_ans",
        value: values.nombre_occupant_0_18_ans,
    })
    send({
        type: "UPDATE_FIELD",
        field: "nombre_occupant_18_60_ans",
        value: values.nombre_occupant_18_60_ans,
    })
    send({
        type: "UPDATE_FIELD",
        field: "nombre_occupant_60_ans",
        value: values.nombre_occupant_60_ans,
    })
    send({
        type: "UPDATE_FIELD",
        field: "surface_logement",
        value: values.surface_logement,
    })

    send({
        type: "UPDATE_FIELD",
        field: "periode_travaux",
        value: [values.start_date, values.end_date],
    })
    send({
        type: "UPDATE_FIELD",
        field: "travaux_types",
        value: selectedTravaux,
    })

    send({ type: "SUBMIT" })
}

const onSubmit = handleSubmit(onSuccess, onError)
const { value: start_date } = useField("start_date")
const { value: end_date } = useField("end_date")
const { value: travaux_realises } = useField<"oui" | "non">("travaux_realises")
const { value: nombre_occupant } = useField("nombre_occupant")
const { value: nombre_occupant_0_18_ans } = useField("nombre_occupant_0_18_ans")
const { value: nombre_occupant_18_60_ans } = useField(
    "nombre_occupant_18_60_ans"
)
const { value: nombre_occupant_60_ans } = useField("nombre_occupant_60_ans")
const { value: surface_logement } = useField("surface_logement")
const { value: travaux_types } = useField<TravauxType[]>("travaux_types")

const datesTravauxLegend = computed(() =>
    travaux_realises.value === "non"
        ? "Date de début et de fin estimatives de l'ensemble des travaux"
        : "Date de début et de fin de l'ensemble des travaux"
)

const startDateLabel = computed(() =>
    travaux_realises.value === "non"
        ? "Date de début estimative des travaux"
        : "Date de début des travaux"
)

const endDateLabel = computed(() =>
    travaux_realises.value === "non"
        ? "Date de fin estimative des travaux"
        : "Date de fin des travaux"
)

const startAndEndDateMax = computed(() =>
    travaux_realises.value === "oui" ? new Date() : undefined
)

const timeCompletion = ref(new Date().getTime())

onActivated(() => {
    timeCompletion.value = new Date().getTime()
    $track(TRACKING_EVENTS.STEP_ENTERED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
    })
})

onDeactivated(() => {
    $track(TRACKING_EVENTS.STEP_COMPLETED, {
        step: snapshot.value.context.step + 1,
        formId: snapshot.value.context.formId,
        timeCompletion: (
            (new Date().getTime() - timeCompletion.value) /
            1000
        ).toFixed(1),
    })
})
</script>

<template>
    <FormCard
        class="form-info-logement-general"
        title="L'essentiel"
        sub-title="Informations générales"
        @submit="
            () => {
                onSubmit()
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'submit-form-info-logement-general',
                    buttonName: 'Suivant',
                })
            }
        "
        @back="
            () => {
                send({ type: 'BACK' })
                $track(TRACKING_EVENTS.CLICK_BUTTON, {
                    buttonId: 'back-form-info-logement-general',
                    buttonName: 'Retour',
                })
            }
        "
        :loading-submit="
            snapshot.matches({
                Step2: { FormInfoLogementGeneral: 'updateField' },
            })
        "
    >
        <fieldset class="form-info-logement-general__work-completed">
            <legend class="text-primary-800 font-bold mb-4">
                Les travaux ont-ils déjà été réalisés ?
            </legend>
            <BaseSelectButton
                class="form-info-logement-general__select"
                v-model="travaux_realises"
                :allow-empty="false"
                :options="travauxRealisesOptions"
                option-label="label"
                option-value="value"
                :pt="{
                    pcToggleButton: {
                        root: 'custom-togglebutton',
                        label: 'custom-togglebutton-label',
                    },
                }"
            />
        </fieldset>

        <!-- Période de travaux -->
        <fieldset class="form-info-logement-general__period">
            <legend class="text-primary-800 font-bold mb-4">
                {{ datesTravauxLegend }}
            </legend>
            <div class="flex gap-4 w-full items-start">
                <BaseInputWrapper
                    class="w-full"
                    :label="startDateLabel"
                    input-id="start_date"
                    :error="errors.start_date"
                >
                    <BaseDatePicker
                        id="start_date"
                        v-model="start_date"
                        :invalid="!!errors.start_date"
                        placeholder="--/--/--"
                        date-format="dd/mm/yy"
                        :max-date="startAndEndDateMax"
                    />
                </BaseInputWrapper>
                <BaseInputWrapper
                    class="w-full"
                    :label="endDateLabel"
                    input-id="end_date"
                    :error="errors.end_date"
                >
                    <BaseDatePicker
                        id="end_date"
                        v-model="end_date"
                        :invalid="!!errors.end_date"
                        placeholder="--/--/--"
                        date-format="dd/mm/yy"
                        :max-date="startAndEndDateMax"
                    />
                </BaseInputWrapper>
            </div>
        </fieldset>

        <fieldset class="form-info-logement-general__travaux">
            <legend class="text-primary-800 font-bold mb-4">
                Quels travaux envisagez-vous ou avez-vous réalisés ?
            </legend>
            <div class="flex flex-col gap-3">
                <MultiSelect
                    v-model="travaux_types"
                    :options="travauxOptionsByCategory"
                    option-label="label"
                    option-value="value"
                    option-group-label="label"
                    option-group-children="items"
                    display="chip"
                    filter
                    :show-toggle-all="false"
                    :max-selected-labels="2"
                    selected-items-label="{0} travaux sélectionnés"
                    placeholder="Sélectionnez un ou plusieurs types de travaux"
                    class="w-full"
                    :invalid="!!errors.travaux_types"
                />
                <BaseMessage v-if="errors.travaux_types" severity="error">
                    {{ errors.travaux_types }}
                </BaseMessage>
            </div>
        </fieldset>

        <!-- Nombre de personne vivant dans le logement et surface -->
        <div class="form-info-logement-general__main-info">
            <fieldset class="form-info-logement-general__count-people">
                <legend class="text-primary-800 font-bold mb-4">
                    Nombre de personnes vivant dans le logement
                </legend>
                <InputCounter
                    input-id="nombre_occupant"
                    :min="0"
                    v-model="nombre_occupant"
                    :error="errors.nombre_occupant"
                    :invalid="!!errors.nombre_occupant"
                    class="w-full md:w-fit"
                />
            </fieldset>

            <fieldset class="form-info-logement-general__surface">
                <legend class="text-primary-800 font-bold mb-4">
                    Surface du logement (m²)
                    <span class="font-light italic">(optionnel)</span>
                </legend>
                <InputCounter
                    input-id="surface_logement"
                    :min="0"
                    v-model="surface_logement"
                    :error="errors.surface_logement"
                    :invalid="!!errors.surface_logement"
                    class="w-full md:w-fit"
                />
            </fieldset>
        </div>
        <!-- Nombre de personnance par tranche d'âges (optionel) -->
        <fieldset class="form-info-logement-general__count-by-age">
            <legend class="text-primary-800 font-bold mb-4">
                Tranches d'âge des personnes vivant dans le logement
                <span class="font-light italic">(optionnel)</span>
            </legend>
            <InputCounter
                input-id="nombre_occupant_0_18_ans"
                label="0-18 ans"
                v-model="nombre_occupant_0_18_ans"
                :min="0"
                :error="errors.nombre_occupant_0_18_ans"
                :invalid="!!errors.nombre_occupant_0_18_ans"
            />
            <InputCounter
                input-id="nombre_occupant_18_60_ans"
                label="18-60 ans"
                v-model="nombre_occupant_18_60_ans"
                :min="0"
                :error="errors.nombre_occupant_18_60_ans"
                :invalid="!!errors.nombre_occupant_18_60_ans"
            />
            <InputCounter
                input-id="nombre_occupant_60_ans"
                label="+ 60 ans"
                v-model="nombre_occupant_60_ans"
                :min="0"
                :error="errors.nombre_occupant_60_ans"
                :invalid="!!errors.nombre_occupant_60_ans"
            />
        </fieldset>
    </FormCard>
</template>

<style scoped>
@import "@styles/breakpoints.css";
.form-info-logement-general {
    --gap-form: 3.5rem;
    --padding-form: 3.5rem;
    &:deep(.p-card-content) {
        display: flex;
        flex-direction: column;
        gap: var(--gap-form);
        padding-top: var(--padding-form);

        @media (--small-viewport) {
            --gap-form: 2.5rem;
            --padding-form: 2.5rem;
        }
    }

    &:deep(.base-input-wrapper) {
        label {
            font-weight: 400;
        }
    }

    .form-info-logement-general__period {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .form-info-logement-general__travaux {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }

    .form-info-logement-general__select {
        &:deep(.custom-togglebutton) {
            width: 7.5rem;
            min-width: 7.5rem;
            max-width: 7.5rem;
            justify-content: center;
        }
    }

    .form-info-logement-general__main-info {
        display: flex;
        gap: 2rem;

        @media (--medium-viewport) {
            flex-direction: column;
            gap: 1.5rem;
        }
    }

    .form-info-logement-general__count-people {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex: 1;
    }

    .form-info-logement-general__surface {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        flex: 1;
    }

    .form-info-logement-general__count-by-age {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;

        @media (--large-viewport) {
            flex-direction: column;
        }
    }

    @media (--small-viewport) {
        width: 100%;
    }
}
</style>
